import { capitalCase } from 'change-case';
import { Button, Col } from 'react-bootstrap';
import { MdChat, MdPhone } from 'react-icons/md';

import HostAndTeamAvatar from './utils/HostAndTeamAvatar';
import useCreateConversationAndRedirect from '@website/components/chat/utils/useCreateConversationAndRedirect';
import { UserBookingDetailsQuery } from '@website/types/graphqlOperations';


export function createMainContactsText(
  team: UserBookingDetailsQuery['booking']['trip']['host']['team'],
): string {
  // a couple of trips do not have any team members, will be looking at why that
  // is the case. The URLs are:
  // 1. trips/raft-american-river
  // 2. trips/remote-hiking-and-swimming
  if (team.items.length === 0) {
    return '';
  }

  if (team.items.length === 1) {
    return `Your head guide is ${team.items[0]?.givenName}`;
  }

  if (team.items.length === 2) {
    return `Your head guides are ${team.items[0]?.givenName} and ${team.items[1]?.givenName}`;
  }

  return `Your head guides are ${team.items[0]?.givenName}, ${team.items[1]?.givenName} and ${team.items[2]?.givenName}`;
}

export function createGuideNamesText(
  team: UserBookingDetailsQuery['booking']['trip']['host']['team'],
): string {
  // a couple of trips do not have any team members, will be looking at why that
  // is the case. The URLs are:
  // 1. trips/raft-american-river
  // 2. trips/remote-hiking-and-swimming
  if (team.items.length === 0) {
    return '';
  }

  if (team.items.length === 1) {
    return `${team.items[0]?.givenName}`;
  }

  if (team.items.length === 2) {
    return `${team.items[0]?.givenName}, ${team.items[1]?.givenName}`;
  }

  return `${team.items[0]?.givenName}, ${team.items[1]?.givenName}, ${team.items[2]?.givenName}`;
}

/**
 * User Booking Details - Host Section
 */
export default function Host({
  booking,
  userID,
}: {
  booking: Pick<UserBookingDetailsQuery['booking'], 'trip' | 'group'>;
  userID: string;
}) {
  const { host } = booking.trip;

  const createChannelAndRedirect = useCreateConversationAndRedirect(
    userID,
    host.id,
  );

  const mainContactsText = createMainContactsText(host.team);

  return (
    <div className="text-block">
      <div className="d-flex flex-column flex-md-row align-items-center mb-3">
        <Col>
          <h5 className="mb-4">Hosted by {host.name}</h5>
          <p className="text-muted small mb-0">
            {host.name} is a fantastic small local guiding company
            {host.businessAddress?.locality
              ? `, based in ${capitalCase(host.businessAddress?.locality)}`
              : ''}
            . As with all guides on Skyhook, they have been carefully vetted for
            safety and quality. {mainContactsText}.
          </p>
        </Col>
        <HostAndTeamAvatar host={host} />
      </div>

      <div className="mb-4">
        <Button
          onClick={() => createChannelAndRedirect()}
          className="mb-2 me-2"
        >
          <label className="my-0 me-2">
            <MdChat size="1.4em" />
          </label>
          Message your guide
          {/* Include the guide name if there is only one of them */}
          {host.team.items.length === 1
            ? ` (${host.team.items?.[0]?.givenName})`
            : ''}
        </Button>

        <a
          href={`tel:${host.contact.phone}`}
          className="btn btn-muted mb-2 me-2"
        >
          <label className="my-0 me-2">
            <MdPhone size="1.4em" />
          </label>
          {host.contact.phone}
        </a>
      </div>
    </div>
  );
}
